interface AudioMixingOptions {
  mainVolume?: number;
  backgroundVolume?: number;
  outputSampleRate?: number;
  outputFormat?: 'wav' | 'mp3';
}

export class AudioMixer {
  private offlineContext: OfflineAudioContext | null = null;

  async mixAudioBuffers(
    mainBuffer: AudioBuffer,
    backgroundBuffer: AudioBuffer,
    options: AudioMixingOptions = {}
  ): Promise<AudioBuffer> {
    const {
      mainVolume = 1.0,
      backgroundVolume = 0.6, // Increased for better audibility
      outputSampleRate = 44100
    } = options;

    // Calculate duration - use the longer of the two audio buffers
    const duration = Math.max(mainBuffer.duration, backgroundBuffer.duration);
    const frameCount = Math.ceil(duration * outputSampleRate);

    // Create offline context for mixing
    this.offlineContext = new OfflineAudioContext(2, frameCount, outputSampleRate);

    // Create source nodes
    const mainSource = this.offlineContext.createBufferSource();
    const backgroundSource = this.offlineContext.createBufferSource();

    // Create gain nodes for volume control
    const mainGain = this.offlineContext.createGain();
    const backgroundGain = this.offlineContext.createGain();

    // Set buffers
    mainSource.buffer = mainBuffer;
    backgroundSource.buffer = backgroundBuffer;

    // Configure background to loop if it's shorter than main audio
    if (backgroundBuffer.duration < mainBuffer.duration) {
      backgroundSource.loop = true;
      backgroundSource.loopEnd = backgroundBuffer.duration;
    }

    // Set volumes
    mainGain.gain.value = mainVolume;
    backgroundGain.gain.value = backgroundVolume;

    // Connect audio graph
    mainSource.connect(mainGain);
    backgroundSource.connect(backgroundGain);
    mainGain.connect(this.offlineContext.destination);
    backgroundGain.connect(this.offlineContext.destination);

    // Start sources
    mainSource.start(0);
    backgroundSource.start(0);

    console.log('🎵 Starting audio mixing process...');
    const mixedBuffer = await this.offlineContext.startRendering();
    console.log('✅ Audio mixing completed');

    return mixedBuffer;
  }

  async createMixedAudioBlob(
    mainAudioUrl: string,
    backgroundAudioUrl: string,
    options: AudioMixingOptions = {}
  ): Promise<Blob> {
    try {
      console.log('🌐 Loading audio buffers for mixing...');
      
      // Load both audio buffers
      const [mainBuffer, backgroundBuffer] = await Promise.all([
        this.loadAudioBuffer(mainAudioUrl),
        this.loadAudioBuffer(backgroundAudioUrl)
      ]);

      console.log('🎛️ Mixing audio buffers...');
      const mixedBuffer = await this.mixAudioBuffers(mainBuffer, backgroundBuffer, options);

      console.log('💾 Converting mixed buffer to blob...');
      const blob = await this.audioBufferToBlob(mixedBuffer, options.outputFormat || 'wav');
      
      return blob;
    } catch (error) {
      console.error('❌ Audio mixing failed:', error);
      throw error;
    }
  }

  private async loadAudioBuffer(url: string): Promise<AudioBuffer> {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    
    // Create a temporary audio context for decoding
    const tempContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const audioBuffer = await tempContext.decodeAudioData(arrayBuffer);
    tempContext.close();
    
    return audioBuffer;
  }

  private async audioBufferToBlob(buffer: AudioBuffer, format: 'wav' | 'mp3' = 'wav'): Promise<Blob> {
    if (format === 'wav') {
      return this.audioBufferToWav(buffer);
    } else {
      // For MP3, we'd need additional encoding - for now, default to WAV
      console.warn('MP3 encoding not implemented, using WAV format');
      return this.audioBufferToWav(buffer);
    }
  }

  private audioBufferToWav(buffer: AudioBuffer): Blob {
    const length = buffer.length;
    const numberOfChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    
    // Calculate buffer size
    const arrayBuffer = new ArrayBuffer(44 + length * numberOfChannels * 2);
    const view = new DataView(arrayBuffer);
    
    // Write WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    const writeUint32 = (offset: number, value: number) => {
      view.setUint32(offset, value, true);
    };
    
    const writeUint16 = (offset: number, value: number) => {
      view.setUint16(offset, value, true);
    };
    
    // RIFF chunk descriptor
    writeString(0, 'RIFF');
    writeUint32(4, 36 + length * numberOfChannels * 2);
    writeString(8, 'WAVE');
    
    // FMT sub-chunk
    writeString(12, 'fmt ');
    writeUint32(16, 16);
    writeUint16(20, 1);
    writeUint16(22, numberOfChannels);
    writeUint32(24, sampleRate);
    writeUint32(28, sampleRate * numberOfChannels * 2);
    writeUint16(32, numberOfChannels * 2);
    writeUint16(34, 16);
    
    // Data sub-chunk
    writeString(36, 'data');
    writeUint32(40, length * numberOfChannels * 2);
    
    // Write audio data
    let offset = 44;
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        offset += 2;
      }
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }
}

export const createAudioMixer = () => new AudioMixer();